"use client";

import { useEffect, useState, Component, ReactNode } from "react";
import { Menu, CircleAlert as AlertTriangle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
class LinkErrorBoundary extends Component<{
  children: ReactNode;
}, {
  hasError: boolean;
}> {
  constructor(props: {
    children: ReactNode;
  }) {
    super(props);
    this.state = {
      hasError: false
    };
  }
  static getDerivedStateFromError() {
    return {
      hasError: true
    };
  }
  componentDidCatch(error: Error, errorInfo: any) {
    console.error("Link Routing Error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return <span className="text-destructive flex items-center gap-1"><AlertTriangle className="size-3" /> Link Error</span>;
    }
    return this.props.children;
  }
}
const validateRoute = (href: string) => {
  const validPages = ['/HomePage', '/Shop', '/Cart', '/Checkout', '/Destinations', '/RequestAQuotePage', '/PopularGetaways', '/BestSellingPackages'];
  if (!href || href === "#" || href.startsWith("http") || href.startsWith("/#")) return true;
  const isValid = validPages.includes(href);
  if (!isValid) {
    console.warn(`Route Validation Warning: The path "${href}" does not match any known page routes. Did you mean "/BestSellingPackages"?`);
  }
  return isValid;
};
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        <div className="h-3 w-16 bg-muted animate-pulse" />
        <div className="h-3 w-20 bg-muted animate-pulse" />
        <div className="h-3 w-16 bg-muted animate-pulse" />
        <div className="h-3 w-16 bg-muted animate-pulse" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6 lg:gap-8">
        {menuItems.map((item, i) => {
        validateRoute(item.href || "");
        return <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <LinkErrorBoundary>
                  <Link to={item.href || "#"} className="relative text-white font-default uppercase text-[11px] tracking-[0.22em] bg-transparent hover:bg-transparent focus:bg-transparent px-0 h-auto py-1 group">
                    <span className="relative inline-block">
                      {item.label}
                      <span aria-hidden="true" className="absolute left-0 -bottom-1 h-px w-0 bg-white transition-all duration-[350ms] ease-out group-hover:w-full" />
                    </span>
                  </Link>
                </LinkErrorBoundary>
              </NavigationMenuLink>
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6">
        <div className="h-4 w-32 bg-muted animate-pulse" />
        <div className="h-4 w-40 bg-muted animate-pulse" />
        <div className="h-4 w-28 bg-muted animate-pulse" />
        <div className="h-4 w-32 bg-muted animate-pulse" />
      </div>;
  }
  return <nav className="flex flex-col gap-2">
      {menuItems.filter(item => !item.parent || item.parent === 0).map((item, i) => {
      validateRoute(item.href || "");
      return <LinkErrorBoundary key={item.id}>
              <Link data-index={i} to={item.href || "#"} onClick={onNavigate} className="relative text-white font-default uppercase text-[11px] tracking-[0.22em] bg-transparent hover:bg-transparent focus:bg-transparent group py-4 px-2 block w-full">
                <span className="relative inline-block">
                  {item.label}
                  <span aria-hidden="true" className="absolute left-0 -bottom-1 h-px w-0 bg-gradient-to-r from-accent to-chart-2 opacity-0 transition-all duration-[350ms] ease-out group-hover:w-full group-hover:opacity-100" />
                </span>
              </Link>
            </LinkErrorBoundary>;
    })}
    </nav>;
}
export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      if (href === "/#") {
        e.preventDefault();
        window.scrollTo({
          top: 0,
          behavior: "smooth"
        });
        return;
      }
      let hash = "";
      if (href.startsWith("#")) {
        hash = href;
      } else if (href.startsWith("/") && href.includes("#")) {
        const [path, hashPart] = href.split("#");
        if (hashPart === "") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        if (path === "/" || path === window.location.pathname) {
          hash = "#" + hashPart;
        }
      }
      if (hash && hash !== "#") {
        const element = document.querySelector(hash);
        if (element) {
          e.preventDefault();
          element.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="1334" id="main-navigation" className="sticky top-0 left-0 right-0 z-50 w-full transition-all duration-300 ease-out text-white backdrop-blur-md border-b border-white/10 bg-primary/19">
      <div className="absolute top-2 left-4 md:left-6 lg:left-8 pointer-events-none select-none">
        <span className="font-default uppercase text-[10px] tracking-[0.22em] text-white/70">
          MARGA TRAVEL — Life is now
        </span>
      </div>

      <div className="mx-auto w-full max-w-[1440px] px-4 !bg-[rgba(0,0,0,0.23)] md:px-8 lg:px-16">
        <div className="grid grid-cols-12 items-center h-16 md:h-20 gap-4">
          <div className="col-span-8 md:col-span-9 flex items-center !w-[149.484375px] !h-[20px]">
            <Link to="/" aria-label="Marga Travel — Home" className="inline-flex items-center">
              <WvcLogo className="h-7 md:h-8 w-auto" />
            </Link>
          </div>

          <div className="hidden md:col-span-3 md:flex items-center justify-end gap-8">
            <MenuProvider menu_id="46">
              <DesktopMenuConsumer />
            </MenuProvider>

            <div className="flex flex-col items-end">
              <Button asChild className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive shadow-xs has-[>svg]:px-3 text-white hover:bg-secondary/90 font-default uppercase text-[11px] tracking-[0.18em] px-6 py-3 h-auto transition-colors duration-250 bg-primary">
                <Link to="/ShopPage">Book Now</Link>
              </Button>
              <div className="mt-2 flex items-center gap-1.5">
                <span className="block h-px w-12 bg-[color:var(--chart-2)]" />
                <span className="block h-1 w-1 rounded-lg bg-[color:var(--chart-2)]" />
              </div>
            </div>
          </div>

          <div className="col-span-4 md:hidden flex items-center justify-end">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu" className="text-white hover:bg-transparent hover:text-[color:var(--chart-2)] rounded-md">
                  <Menu className="size-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-primary border-l border-white/10 w-full sm:max-w-sm p-0 flex flex-col z-[100] max-h-screen overflow-y-auto overflow-hidden">
                <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                <SheetDescription className="sr-only">
                  Marga Travel primary navigation
                </SheetDescription>

                <div className="px-6 pt-6 pb-4 border-b border-white/10">
                  <span className="font-default uppercase text-[10px] tracking-[0.22em] text-white/70">
                    MARGA TRAVEL — Life is now
                  </span>
                </div>

                <div className="flex-1 px-6 py-10 overflow-y-auto">
                  <MenuProvider menu_id="46">
                    <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
                  </MenuProvider>

                  <div className="mt-12 relative z-10">
                    <Button asChild className="w-full bg-secondary text-white hover:bg-secondary/90 font-default uppercase text-[11px] tracking-[0.18em] py-4 h-auto shadow-lg">
                      <Link to="/ShopPage" onClick={() => setMobileOpen(false)}>
                        Book Now
                      </Link>
                    </Button>
                  </div>
                </div>

                <div className="px-6 pb-8 pt-4 border-t border-white/10">
                  <div className="flex items-center gap-1.5">
                    <span className="block h-px w-24 bg-[color:var(--chart-2)]" />
                    <span className="block h-1 w-1 rounded-lg bg-[color:var(--chart-2)]" />
                  </div>
                  <p className="mt-3 font-default uppercase text-[10px] tracking-[0.22em] text-white/70">
                    Curated Travel Experiences
                  </p>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}